import React, { useState, useRef, useEffect } from 'react';
import { getOptimizedImageUrl, createBlurPlaceholder, generateSrcSet } from '../utils/imageOptimization';

const LazyImage = ({
  src,
  alt = '',
  className = '',
  style = {},
  width,
  height,
  quality = 'auto:good',
  placeholder = true,
  responsive = false,
  objectFit = 'cover',
  onLoad,
  onError,
  ...props
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = (e) => {
    setLoaded(true);
    setError(false);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setError(true);
    setLoaded(false);
    if (onError) onError(e);
  };

  // Optimize image URL
  const optimizedSrc = getOptimizedImageUrl(src, {
    width,
    height,
    quality
  });

  // Create blur placeholder if enabled
  const blurPlaceholder = placeholder ? createBlurPlaceholder(src) : null;

  // Generate srcSet for responsive images
  const srcSet = responsive ? generateSrcSet(src) : null;

  // Sizes attribute for responsive images
  const sizes = responsive
    ? '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
    : undefined;

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${className}`}
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        ...style
      }}
    >
      {/* Blur placeholder */}
      {placeholder && blurPlaceholder && !loaded && inView && (
        <img
          src={blurPlaceholder}
          alt=""
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(10px)',
            transform: 'scale(1.1)',
            zIndex: 1
          }}
          aria-hidden="true"
        />
      )}

      {/* Main image */}
      {inView && (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: 'auto',
            height: 'auto',
            maxWidth: '100%',
            maxHeight: '80vh',
            objectFit: 'contain',
            display: 'block',
            margin: '0 auto',
            transition: 'opacity 0.3s ease',
            opacity: loaded ? 1 : 0
          }}
          {...props}
        />
      )}

      {/* Error fallback */}
      {error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            color: '#6c757d',
            fontSize: '14px',
            zIndex: 3
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21,15 16,10 5,21"/>
          </svg>
        </div>
      )}

      {/* Loading placeholder */}
      {!inView && (
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid #e9ecef',
              borderTop: '2px solid #da5913',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LazyImage;